
import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Box, Typography, TextField, Button, Divider, Paper, Stack,
  Alert, CircularProgress, useMediaQuery, useTheme,
} from "@mui/material";
import AddPhotoAlternateIcon from "@mui/icons-material/AddPhotoAlternate";
import VideoLibraryIcon from "@mui/icons-material/VideoLibrary";
import LinkIcon from "@mui/icons-material/Link";
import PublishIcon from "@mui/icons-material/Publish";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import EditIcon from "@mui/icons-material/Edit";
import CloseIcon from "@mui/icons-material/Close";
import TopBar from "./topbar";
import Footer from "./footer";

// ── Brand palette ────────────────────────────────────────────────────────────
const C = {
  primary: "#0A2E8A",
  primaryDark: "#061E5C",
  primaryLight: "#3B5BC9",
  primarySoft: "#E8EDFC",
  primarySofter: "#F2F5FE",
  secondary: "#10682A",
  secondaryDark: "#0A4A1C",
  secondaryLight: "#2E9148",
  secondarySoft: "#E3F2E8",
  bg: "#FAFBFC",
  paper: "#FFFFFF",
  border: "#E5E9F0",
  borderSoft: "#EEF1F6",
  textPrimary: "#0D1117",
  textSecondary: "#5B6B7E",
  textMuted: "#8A9BB0",
  error: "#DC2626",
  errorSoft: "#FEE2E2",
};

const API = "https://backend-global-1vq9.onrender.com";

const fieldSx = {
  "& .MuiOutlinedInput-root": {
    borderRadius: 2.5, fontSize: 14,
    bgcolor: C.paper,
    "& fieldset": { borderColor: C.border },
    "&:hover fieldset": { borderColor: C.primary },
    "&.Mui-focused fieldset": { borderColor: C.primary, borderWidth: 2 },
  },
  "& .MuiInputLabel-root.Mui-focused": { color: C.primary },
};

const Community = () => {
  const [title, setTitle] = useState("");
  const [category, setCategory] = useState("");
  const [description, setDescription] = useState("");
  const [mediaType, setMediaType] = useState("image");
  const [mediaFile, setMediaFile] = useState(null);
  const [mediaPreview, setMediaPreview] = useState(null);
  const [mediaUrl, setMediaUrl] = useState("");
  const [posting, setPosting] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  // File upload handler
  const handleFileChange = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      setMediaFile(file);
      setMediaPreview(URL.createObjectURL(file));
    }
    // Reset input so the same file can be re-selected
    e.target.value = "";
  };

  const clearMedia = () => {
    setMediaFile(null);
    setMediaPreview(null);
  };

  // Upload file to backend
  const uploadFile = async () => {
    if (!mediaFile) return null;
    const formData = new FormData();
    formData.append("file", mediaFile);

    try {
      const res = await fetch(`${API}/api/upload`, {
        method: "POST",
        body: formData,
      });
      if (!res.ok) throw new Error("File upload failed");
      const data = await res.json();
      return data.mediaUrl;
    } catch (err) {
      setError("Upload error: " + err.message);
      return null;
    }
  };

  // Create Post
  const handleCreatePost = async () => {
    setError("");
    setSuccess("");

    const user = JSON.parse(localStorage.getItem("user"));
    if (!user || !user.id) {
      setError("User not found. Please log in again.");
      return;
    }

    if (!title || !category || !description) {
      setError("Please fill in all required fields.");
      return;
    }

    setPosting(true);
    let uploadedUrl = mediaUrl;

    if (mediaFile) {
      uploadedUrl = await uploadFile();
      if (!uploadedUrl) {
        setPosting(false);
        return;
      }
    }

    const newPost = {
      title,
      category,
      description,
      mediaType,
      mediaUrl: uploadedUrl || "",
      createdBy: user.id,
    };

    try {
      const res = await fetch(`${API}/api/posts`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(newPost),
      });

      if (!res.ok) throw new Error("Failed to create post");
      await res.json();
      setSuccess("Post created successfully! Redirecting…");
      setTitle("");
      setCategory("");
      setDescription("");
      setMediaUrl("");
      clearMedia();
      // Take the user to the all-posts page after a brief success flash
      setTimeout(() => navigate("/discussionForum"), 900);
    } catch (err) {
      setError(err.message);
    } finally {
      setPosting(false);
    }
  };

  const MediaTypeButton = ({ value, label, icon }) => {
    const active = mediaType === value;
    return (
      <Button
        variant={active ? "contained" : "outlined"}
        startIcon={icon}
        size={isMobile ? "small" : "medium"}
        onClick={() => setMediaType(value)}
        sx={{
          textTransform: "none",
          fontWeight: 700, fontSize: 13,
          borderRadius: 2.5,
          flex: isMobile ? "1 1 auto" : "unset",
          ...(active
            ? {
                background: `linear-gradient(135deg, ${C.primary} 0%, ${C.primaryDark} 100%)`,
                color: "#fff",
                boxShadow: "none",
                "&:hover": {
                  background: `linear-gradient(135deg, ${C.primaryDark} 0%, ${C.primary} 100%)`,
                  boxShadow: `0 4px 12px ${C.primary}30`,
                },
              }
            : {
                borderColor: C.border, color: C.textSecondary,
                "&:hover": { borderColor: C.primary, color: C.primary, bgcolor: C.primarySofter },
              }),
        }}
      >
        {label}
      </Button>
    );
  };

  return (
    <Box sx={{ display: "flex", flexDirection: "column", minHeight: "100vh", bgcolor: C.bg }}>
      <TopBar />

      <Box sx={{ flex: 1, px: { xs: 2, sm: 3, md: 5, lg: 10 }, py: { xs: 3, md: 4 } }}>

        {/* ── Back nav ─────────────────────────────────────────────── */}
        <Button
          startIcon={<ArrowBackIcon sx={{ fontSize: 18 }} />}
          onClick={() => navigate("/discussionForum")}
          sx={{
            color: C.secondary, fontWeight: 700, fontSize: 13,
            textTransform: "none",
            mb: 2, ml: -1,
            borderRadius: 2,
            px: 1.5,
            "&:hover": { bgcolor: C.secondarySoft, color: C.secondaryDark },
          }}
        >
          Back to All Posts
        </Button>

        {/* ── Hero header ────────────────────────────────────────── */}
        <Paper
          elevation={0}
          sx={{
            position: "relative",
            borderRadius: 3, overflow: "hidden",
            background: `linear-gradient(135deg, ${C.primary} 0%, ${C.primaryDark} 100%)`,
            p: { xs: 3, sm: 4 },
            mb: { xs: 2.5, sm: 3 },
            color: "#fff",
            boxShadow: `0 6px 24px ${C.primary}30`,
          }}
        >
          <Box sx={{
            position: "absolute",
            top: "-40%", right: "-10%",
            width: { xs: 200, sm: 280 }, height: { xs: 200, sm: 280 },
            borderRadius: "50%",
            background: `radial-gradient(circle, ${C.secondary}40 0%, transparent 70%)`,
            pointerEvents: "none",
          }} />
          <Box sx={{ position: "relative", zIndex: 1 }}>
            <Stack direction="row" alignItems="center" spacing={1.25} mb={1}>
              <EditIcon sx={{ fontSize: 20, color: "rgba(255,255,255,0.85)" }} />
              <Typography sx={{
                fontSize: { xs: 11, sm: 12 }, fontWeight: 700, letterSpacing: 1.5,
                textTransform: "uppercase", color: "rgba(255,255,255,0.75)",
              }}>
                Discussion Forum
              </Typography>
            </Stack>
            <Typography sx={{
              fontWeight: 800, fontSize: { xs: 22, sm: 28, md: 32 },
              color: "#fff", lineHeight: 1.15, mb: 0.75, letterSpacing: -0.5,
            }}>
              Create a Post
            </Typography>
            <Typography sx={{
              color: "rgba(255,255,255,0.85)",
              fontSize: { xs: 13, sm: 14 }, maxWidth: 540, lineHeight: 1.5,
            }}>
              Share an idea, ask a question, or start a discussion with the community
            </Typography>
          </Box>
        </Paper>

        {/* ── Create-post form ─────────────────────────────────────── */}
        <Paper
          elevation={0}
          sx={{
            position: "relative",
            maxWidth: 820,
            mx: "auto",
            borderRadius: 3,
            border: `1px solid ${C.border}`,
            bgcolor: C.paper,
            p: { xs: 2.5, sm: 4 },
            boxShadow: "0 2px 12px rgba(13,17,23,0.04)",
            overflow: "hidden",
            "&::before": {
              content: '""',
              position: "absolute",
              top: 0, left: 0, right: 0,
              height: "3px",
              background: `linear-gradient(90deg, ${C.primary}, ${C.primaryLight})`,
            },
          }}
        >
          <Stack direction="row" alignItems="center" spacing={1.25} mb={2} mt={0.5}>
            <Box sx={{
              width: 4, height: 24,
              background: `linear-gradient(135deg, ${C.primary}, ${C.primaryLight})`,
              borderRadius: 1,
            }} />
            <Typography sx={{ fontWeight: 700, fontSize: 16, color: C.textPrimary }}>
              Post Details
            </Typography>
          </Stack>
          <Divider sx={{ mb: 3, borderColor: C.borderSoft }} />

          {error && (
            <Alert
              severity="error" onClose={() => setError("")}
              sx={{ mb: 2.5, borderRadius: 2.5, fontSize: 13 }}
            >
              {error}
            </Alert>
          )}
          {success && (
            <Alert severity="success" sx={{ mb: 2.5, borderRadius: 2.5, fontSize: 13 }}>
              {success}
            </Alert>
          )}

          <Stack spacing={2.5}>
            <TextField
              fullWidth
              label="Title *"
              placeholder="Give your post a clear, descriptive title"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              sx={fieldSx}
              size="small"
            />

            <TextField
              fullWidth
              label="Category *"
              placeholder="e.g. Technology, Manufacturing, AI and Art"
              value={category}
              onChange={(e) => setCategory(e.target.value)}
              sx={fieldSx}
              size="small"
            />

            <TextField
              fullWidth
              multiline
              minRows={5}
              label="Description *"
              placeholder="Share the details of your post — what's on your mind?"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              sx={fieldSx}
              size="small"
            />

            {/* ── Media section ──────────────────────────────────────── */}
            <Box>
              <Stack direction="row" alignItems="center" spacing={1.25} mb={1.5}>
                <Box sx={{
                  width: 4, height: 18,
                  background: `linear-gradient(135deg, ${C.secondary}, ${C.secondaryLight})`,
                  borderRadius: 1,
                }} />
                <Typography sx={{ fontWeight: 700, fontSize: 14, color: C.textPrimary }}>
                  Add Media
                </Typography>
                <Typography sx={{ fontSize: 11, color: C.textMuted, fontWeight: 600 }}>
                  optional
                </Typography>
              </Stack>

              <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap mb={2}>
                <MediaTypeButton value="image" label="Image" icon={<AddPhotoAlternateIcon />} />
                <MediaTypeButton value="video" label="Video" icon={<VideoLibraryIcon />} />
                <MediaTypeButton value="link"  label="External Link" icon={<LinkIcon />} />
              </Stack>

              {mediaType === "link" ? (
                <TextField
                  fullWidth
                  label="Media URL"
                  placeholder="https://..."
                  value={mediaUrl}
                  onChange={(e) => setMediaUrl(e.target.value)}
                  size="small"
                  sx={fieldSx}
                />
              ) : (
                <Box>
                  {/* File picker drop zone */}
                  <Box
                    component="label"
                    htmlFor="media-upload-input"
                    sx={{
                      display: "flex", alignItems: "center", gap: 1.5,
                      p: 2, borderRadius: 2.5, cursor: "pointer",
                      border: `2px dashed ${mediaFile ? C.primary : C.border}`,
                      bgcolor: mediaFile ? C.primarySoft : C.primarySofter,
                      transition: "border-color 0.2s, background 0.2s",
                      "&:hover": { borderColor: C.primary, bgcolor: C.primarySoft },
                    }}
                  >
                    <UploadFileIcon sx={{
                      fontSize: 26,
                      color: mediaFile ? C.primary : C.textMuted, flexShrink: 0,
                    }} />
                    <Box flex={1} minWidth={0}>
                      {mediaFile ? (
                        <>
                          <Typography sx={{ fontSize: 13, fontWeight: 700, color: C.primary }} noWrap>
                            {mediaFile.name}
                          </Typography>
                          <Typography sx={{ fontSize: 11, color: C.textSecondary }}>
                            {(mediaFile.size / 1024 / 1024).toFixed(2)} MB · click to replace
                          </Typography>
                        </>
                      ) : (
                        <>
                          <Typography sx={{ fontSize: 13, fontWeight: 700, color: C.textPrimary }}>
                            Click to upload {mediaType === "image" ? "an image" : "a video"}
                          </Typography>
                          <Typography sx={{ fontSize: 11, color: C.textMuted }}>
                            {mediaType === "image" ? "PNG, JPG, GIF" : "MP4, WebM"}
                          </Typography>
                        </>
                      )}
                    </Box>
                    {mediaFile && (
                      <Button
                        size="small"
                        onClick={(e) => { e.preventDefault(); clearMedia(); }}
                        sx={{
                          minWidth: 0, p: 0.5, color: C.error,
                          "&:hover": { bgcolor: C.errorSoft },
                        }}
                      >
                        <CloseIcon sx={{ fontSize: 18 }} />
                      </Button>
                    )}
                    <input
                      id="media-upload-input"
                      type="file"
                      accept={mediaType === "image" ? "image/*" : "video/*"}
                      hidden
                      onChange={handleFileChange}
                    />
                  </Box>

                  {/* Preview */}
                  {mediaPreview && (
                    <Box
                      sx={{
                        mt: 2,
                        borderRadius: 2.5,
                        overflow: "hidden",
                        height: { xs: 180, sm: 240 },
                        bgcolor: C.borderSoft,
                        border: `1px solid ${C.border}`,
                      }}
                    >
                      {mediaType === "image" ? (
                        <img
                          src={mediaPreview}
                          alt="Preview"
                          style={{ width: "100%", height: "100%", objectFit: "cover" }}
                        />
                      ) : (
                        <video
                          src={mediaPreview}
                          controls
                          style={{ width: "100%", height: "100%", objectFit: "cover" }}
                        />
                      )}
                    </Box>
                  )}
                </Box>
              )}
            </Box>

            <Typography sx={{ fontSize: 11, color: C.textMuted, fontWeight: 600 }}>
              * Required fields
            </Typography>

            <Button
              fullWidth
              variant="contained"
              startIcon={!posting && <PublishIcon sx={{ fontSize: 18 }} />}
              onClick={handleCreatePost}
              disabled={posting}
              sx={{
                background: `linear-gradient(135deg, ${C.primary} 0%, ${C.primaryDark} 100%)`,
                color: "#fff",
                borderRadius: 2.5,
                textTransform: "none",
                py: 1.5, fontWeight: 700, fontSize: 15,
                boxShadow: "none",
                "&:hover": { boxShadow: `0 4px 14px ${C.primary}40` },
                "&.Mui-disabled": { background: "#CCC", color: "#fff" },
              }}
            >
              {posting
                ? <Stack direction="row" spacing={1} alignItems="center">
                    <CircularProgress size={18} sx={{ color: "#fff" }} />
                    <span>Publishing…</span>
                  </Stack>
                : "Publish Post"}
            </Button>
          </Stack>
        </Paper>
      </Box>

      <Footer />
    </Box>
  );
};

export default Community;